"use client";

import { useCallback, useEffect, useState } from "react";
import { createClient } from "@/utils/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { Bell, X } from "lucide-react";

import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import { UserAvatar } from "@/components/shared/UserAvatar";

// Ensure Notification type matches the structure of data
type Notification = {
  content: string;
  created_at: string;
  id: string;
  is_read: boolean;
  metadata: { sender_id?: string; user_id?: string } | null; // Updated to include both sender_id and user_id
  type: string;
  updated_at: string;
  user_id: string;
};

interface NotificationsDropdownProps {
  userId: string;
}

export default function NotificationsDropdown({
  userId,
}: NotificationsDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const supabase = createClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const handleDropdownOpen = useCallback(
    async (open: boolean) => {
      setIsOpen(open);
      if (open && unreadCount > 0) {
        // Mark all notifications as read when opening the dropdown
        const { error } = await supabase
          .from("notifications")
          .update({ is_read: true })
          .eq("user_id", userId)
          .eq("is_read", false);

        if (error) {
          console.error("Error marking notifications as read:", error);
        } else {
          setUnreadCount(0);
          setNotifications((prev) =>
            prev.map((notif) => ({ ...notif, is_read: true })),
          );
        }
      }
    },
    [supabase, userId, unreadCount],
  );

  const fetchNotifications = useCallback(async () => {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching notifications:", error);
    } else {
      setNotifications(data as Notification[]);
      setUnreadCount(
        data.filter((notif: Notification) => !notif.is_read).length,
      );
    }
  }, [supabase, userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const clearAllNotifications = async () => {
    const { error } = await supabase.rpc(
      "clear_notifications_except_pending" as any,
      {
        p_user_id: userId,
      },
    );

    if (error) {
      console.error("Error clearing notifications:", error);
      toast({
        title: "Error",
        description: "Failed to clear notifications. Please try again.",
        variant: "destructive",
      });
    } else {
      // Refresh notifications
      await fetchNotifications();
      toast({
        title: "Notifications cleared",
        description:
          "All notifications have been cleared except pending friend requests.",
      });
    }
  };

  const dismissNotification = async (notificationId: string) => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);

    if (error) {
      console.error("Error dismissing notification:", error);
      toast({
        title: "Error",
        description: "Failed to dismiss notification. Please try again.",
        variant: "destructive",
      });
    } else {
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  const handleFriendRequestResponse = async (
    notificationId: string,
    senderId: string | undefined,
    accept: boolean,
  ) => {
    if (!senderId) {
      console.error("Sender ID is missing");
      toast({
        title: "Error",
        description: "Unable to process the friend request. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await fetch("/api/friend-request/respond", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notificationId, senderId, accept }),
      });

      if (!response.ok) {
        throw new Error("Failed to respond to friend request");
      }

      // Remove the notification from the local state
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== notificationId),
      );

      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));

      if (accept) {
        // Fetch the sender's profile
        const { data: senderProfile, error: profileError } = await supabase
          .from("profiles")
          .select("username, avatar_url")
          .eq("id", senderId)
          .single();

        if (profileError) {
          console.error("Error fetching sender profile:", profileError);
          throw new Error("Failed to fetch sender profile");
        }

        toast({
          title: "Friend request accepted",
          description: (
            <div className="flex items-center space-x-2">
              <UserAvatar
                user={{
                  id: senderId,
                  avatar_url: senderProfile.avatar_url,
                  username: senderProfile.username,
                }}
                size="sm"
              />
              <span>You are now friends with {senderProfile.username}!</span>
            </div>
          ),
        });
      } else {
        toast({
          title: "Friend request rejected",
          description: "The friend request has been rejected.",
        });
      }
    } catch (error) {
      console.error("Error responding to friend request:", error);
      toast({
        title: "Error",
        description: "Failed to respond to friend request. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" strokeWidth="1" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-red-500 font-mono text-xs font-light">
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 rounded-xl">
        <div className="flex items-center justify-between border-b p-2">
          <h3 className="font-semibold">Notifications</h3>
        </div>
        {notifications.length === 0 ? (
          <DropdownMenuItem>No notifications</DropdownMenuItem>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onDismiss={dismissNotification}
                onRespond={handleFriendRequestResponse}
                setIsOpen={setIsOpen}
              />
            ))}
            <DropdownMenuItem
              className="flex items-center justify-center border-t p-2 text-xs text-gray-500"
              onClick={clearAllNotifications}
            >
              Mark all as read
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onRespond: (
    id: string,
    senderId: string | undefined,
    accept: boolean,
  ) => void;
  setIsOpen: (isOpen: boolean) => void;
}

function NotificationItem({
  notification,
  onDismiss,
  onRespond, // Add this line
  setIsOpen,
}: NotificationItemProps) {
  const { profile, isLoading } = useProfile(
    notification.metadata?.user_id || notification.metadata?.sender_id,
  );

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  const handleNotificationClick = async () => {
    if (notification.type === "comment_reply") {
      window.location.href = `/api/notification-redirect?id=${notification.id}`;
    } else {
      // Handle other notification types...
    }
    setIsOpen(false);
  };

  return (
    <DropdownMenuItem
      className="flex items-start space-x-2 p-2"
      onSelect={(e) => {
        e.preventDefault();
        handleNotificationClick();
      }}
    >
      <UserAvatar
        user={{
          id:
            notification.metadata?.user_id ||
            notification.metadata?.sender_id ||
            "",
          avatar_url: profile?.avatar_url,
          username: profile?.username || notification.content.split(" ")[0],
        }}
        size="lg"
        linkToProfile={true}
        isLoading={isLoading}
      />
      <div className="flex-1">
        <p className="text-xs">{notification.content}</p>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })}
        </p>
        {notification.type === "friend_request" && (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              className="bg-emerald-500 text-white hover:bg-emerald-600"
              onClick={(e) =>
                handleButtonClick(e, () =>
                  onRespond(
                    notification.id,
                    notification.metadata?.sender_id,
                    true,
                  ),
                )
              }
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={(e) =>
                handleButtonClick(e, () =>
                  onRespond(
                    notification.id,
                    notification.metadata?.sender_id,
                    false,
                  ),
                )
              }
            >
              Decline
            </Button>
          </div>
        )}
      </div>
    </DropdownMenuItem>
  );
}
